import { useState } from "react";
import { AvatarPreset, Button, ResultCelebration } from "@ec2/ui";
import { useT } from "@ec2/i18n/react";
import type { ScanResult } from "@ec2/contracts";
import type { LocalizedError } from "../lib/errors";
import { safeExternalHref } from "../lib/links";
import { GroupBonusView } from "./GroupBonusView";
import { Sheet } from "./Sheet";

export type ScanOutcome =
  | { status: "result"; result: Exclude<ScanResult, { kind: "quest_open" }> }
  | { status: "error"; error: LocalizedError };

/**
 * The scanner's result sheet (brief §6): celebration for quest solves,
 * connection card for contacts, friendly passthroughs for join/order codes,
 * localized errors for everything else — a decode is never silently ignored.
 */
export function ScanResultSheet({
  outcome,
  onScanAgain,
  onClose,
  onJoin,
}: {
  outcome: ScanOutcome;
  onScanAgain: () => void;
  onClose: () => void;
  /** kind=join: the participant confirmed switching/joining that event. */
  onJoin?: () => void;
}) {
  const t = useT();
  // §5: one celebration at a time — the group bonus follows the quest's own.
  const [showGroupBonus, setShowGroupBonus] = useState(false);

  if (outcome.status === "error") {
    return (
      <Sheet label={t(outcome.error.key, outcome.error.params)} onClose={onClose}>
        <div className="flex flex-col gap-4" data-testid="ec-scan-error">
          <p role="alert" className="pr-8 text-base text-fg">
            {t(outcome.error.key, outcome.error.params)}
          </p>
          <Button variant="primary" onClick={onScanAgain}>
            {t("ec2.scanner.scanAgain")}
          </Button>
        </div>
      </Sheet>
    );
  }

  const result = outcome.result;
  switch (result.kind) {
    case "quest_solved": {
      // Defense-in-depth: the backend restricts redirectUrl to http(s), but
      // never open anything else from the client either.
      const redirectHref = safeExternalHref(result.redirectUrl);
      if (showGroupBonus && result.groupBonus) {
        return (
          <Sheet label={t("ec2.group.celebration.title")} onClose={onClose}>
            <GroupBonusView bonus={result.groupBonus} onDone={onClose} />
          </Sheet>
        );
      }
      return (
        <Sheet label={t("ec2.celebration.solvedToast")} onClose={onClose}>
          <ResultCelebration
            status="solved"
            title={t("ec2.celebration.solvedToast")}
            message={result.questName}
            amount={result.bucksAwarded}
            amountLabel={t("ec2.celebration.earned", { amount: result.bucksAwarded })}
            actionLabel={redirectHref ? t("ec2.scanner.openLink") : t("ec2.celebration.backToQuests")}
            onAction={() => {
              if (redirectHref) {
                window.open(redirectHref, "_blank", "noopener,noreferrer");
              }
              if (result.groupBonus) {
                setShowGroupBonus(true);
                return;
              }
              onClose();
            }}
          />
        </Sheet>
      );
    }
    case "contact_added":
      return (
        <Sheet label={t("ec2.scanner.contact.title")} onClose={onClose}>
          <div className="flex flex-col items-center gap-3 text-center" data-testid="ec-scan-contact">
            <AvatarPreset preset={result.avatarPresetId} size={64} label={t("ec2.a11y.avatar", { name: result.displayName })} />
            <h2 className="font-display text-[21px] font-[650] text-fg">{t("ec2.scanner.contact.title")}</h2>
            <p className="text-base text-fg-secondary">
              {t("ec2.scanner.contact.body", { name: result.displayName, bucks: result.bucksAwarded })}
            </p>
            <Button variant="primary" className="w-full" onClick={onClose}>
              {t("ec2.common.close")}
            </Button>
          </div>
        </Sheet>
      );
    case "join":
      return (
        <Sheet label={t("ec2.scanner.join.title")} onClose={onClose}>
          <div className="flex flex-col gap-3" data-testid="ec-scan-join">
            <h2 className="pr-8 font-display text-[21px] font-[650] text-fg">{t("ec2.scanner.join.title")}</h2>
            <p className="text-base text-fg-secondary">{t("ec2.scanner.join.body", { name: result.eventName })}</p>
            <div className="flex items-center justify-between gap-3">
              <Button variant="ghost" onClick={onScanAgain}>
                {t("ec2.scanner.scanAgain")}
              </Button>
              {onJoin && (
                <Button variant="primary" onClick={onJoin} data-testid="ec-scan-join-cta">
                  {t("ec2.scanner.join.cta")}
                </Button>
              )}
            </div>
          </div>
        </Sheet>
      );
    case "order":
      return (
        <Sheet label={t("ec2.scanner.order.title")} onClose={onClose}>
          <div className="flex flex-col gap-3" data-testid="ec-scan-order">
            <h2 className="pr-8 font-display text-[21px] font-[650] text-fg">{t("ec2.scanner.order.title")}</h2>
            <p className="text-base text-fg-secondary">{t("ec2.scanner.order.body")}</p>
            <p className="font-display text-2xl font-bold tracking-[0.06em] text-fg tabular-nums">{result.orderCode}</p>
            <Button variant="primary" onClick={onClose}>
              {t("ec2.common.close")}
            </Button>
          </div>
        </Sheet>
      );
  }
}
